import React from 'react';
import { Dialog, Box, IconButton, Typography, Stack, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useStatsTheme } from '../../v2/theme/statsTheme';

/**
 * Dialog standardise pour les ecrans cantera.
 * - Full-screen sur mobile (xs)
 * - Centre max-width sur desktop
 * - Header avec titre + close, footer actions optionnel
 *
 * Props:
 *  - open, onClose
 *  - title?: string | ReactNode
 *  - subtitle?: string
 *  - actions?: ReactNode (footer)
 *  - maxWidth?: 'xs' | 'sm' | 'md' | 'lg' (default 'sm')
 *  - tone?: 'dark' | 'light' (default dark)
 *  - hideClose?: boolean
 *  - disableBackdropClose?: boolean
 */
export default function CanteraDialog({
  open,
  onClose,
  title,
  subtitle,
  actions,
  maxWidth = 'sm',
  tone = 'dark',
  hideClose = false,
  disableBackdropClose = false,
  children,
}) {
  const theme = useStatsTheme();
  const muiTheme = useTheme();
  const fullScreen = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const isDark = tone === 'dark';

  const bg = isDark ? theme.surfaces.elevated : '#FFFFFF';
  const fg = isDark ? theme.text.primary : '#111827';
  const fgSecondary = isDark ? theme.text.secondary : '#6B7280';
  const border = isDark ? theme.surfaces.border : 'rgba(0,0,0,0.08)';

  const handleClose = (e, reason) => {
    if (disableBackdropClose && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
    onClose?.(e, reason);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: bg,
          color: fg,
          borderRadius: fullScreen ? 0 : `${theme.radius.xl}px`,
          border: `1px solid ${border}`,
          boxShadow: theme.shadows.lg,
          backgroundImage: 'none',
        },
      }}
      BackdropProps={{
        sx: {
          bgcolor: theme.surfaces.overlay,
          backdropFilter: 'blur(4px)',
        },
      }}
    >
      {(title || !hideClose) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 2.5,
            py: 1.75,
            borderBottom: `1px solid ${border}`,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {title && (
              <Typography sx={{ color: fg, fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em' }}>
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography sx={{ color: fgSecondary, fontSize: 12, fontWeight: 500, mt: 0.25 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {!hideClose && (
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: fg,
                border: `1px solid ${border}`,
                borderRadius: `${theme.radius.md}px`,
                '&:hover': { bgcolor: theme.accentSoft, borderColor: theme.accentBorder },
                transition: theme.motion.fast,
              }}
              aria-label="close"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}

      <Box sx={{ px: 2.5, py: 2, flex: 1, overflowY: 'auto' }}>
        {children}
      </Box>

      {actions && (
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: `1px solid ${border}`,
            bgcolor: isDark ? theme.surfaces.base : '#F9FAFB',
          }}
        >
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {actions}
          </Stack>
        </Box>
      )}
    </Dialog>
  );
}
